import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../contexts/ToastContext';
import apiService from '../../services/apiService';
import EvidenciaUpload from './EvidenciaUpload';
import ConfirmDialog from '../common/ConfirmDialog';
import useAuthenticatedImageLoader from '../../hooks/useAuthenticatedImageLoader';

/**
 * Thumbnail individual de evidencia con lazy loading y cola de concurrencia.
 * Usa useAuthenticatedImageLoader para evitar carga paralela masiva (429).
 *
 * @param {Object} props
 * @param {Object} props.ev - Objeto de evidencia
 * @param {Function} props.onView - Callback (ev, blobUrl) al hacer clic
 * @param {Function} props.onEdit - Callback (ev) al editar descripción
 * @param {Function} props.onDelete - Callback (id) al eliminar
 * @param {boolean} props.disabled - Si las acciones están deshabilitadas
 * @param {boolean} props.canEdit - Si puede editar
 * @param {boolean} props.canDelete - Si puede eliminar
 * @returns {JSX.Element}
 */
function EvidenciaThumb({ ev, onView, onEdit, onDelete, disabled, canEdit, canDelete }) {
  const isImage = ev.mime_type?.startsWith('image/');
  const { ref, blobUrl, loading, failed } = useAuthenticatedImageLoader(
    () => apiService.downloadEvidencia(ev.id),
    ev.id
  );

  const renderMedia = () => {
    if (!isImage) {
      return (
        <div className="evidencia-video-thumb">
          <i className="fas fa-video"></i>
        </div>
      );
    }
    if (failed) {
      return (
        <div className="evidencia-broken-thumb" title={ev.descripcion || 'Imagen no disponible'}>
          <i className="fas fa-image"></i>
        </div>
      );
    }
    if (loading || !blobUrl) {
      return <div className="evidencia-loading-thumb"><i className="fas fa-spinner fa-spin"></i></div>;
    }
    return <img src={blobUrl} alt="" className="evidencia-thumb" />;
  };

  return (
    <div
      ref={ref}
      className="evidencia-item"
      onClick={() => blobUrl && onView(ev, blobUrl)}
    >
      {renderMedia()}
      {!disabled && (
        <div className="evidencia-item-overlay">
          <button
            className="evidencia-item-action-btn evidencia-btn-expand"
            onClick={(e) => { e.stopPropagation(); onView(ev, blobUrl); }}
            title="Ver"
          >
            <i className="fas fa-expand"></i>
          </button>
          {canEdit && (
            <button
              className="evidencia-item-action-btn evidencia-btn-edit-action"
              onClick={(e) => { e.stopPropagation(); onEdit(ev); }}
              title="Editar descripción"
            >
              <i className="fas fa-pencil"></i>
            </button>
          )}
          {canDelete && (
            <button
              className="evidencia-item-action-btn evidencia-btn-delete-action"
              onClick={(e) => { e.stopPropagation(); onDelete(ev.id); }}
              title="Eliminar"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Galeria de evidencia por actividad con upload integrado.
 * Muestra thumbnails separados en Antes/Despues con indicador de completitud.
 * Las imágenes se cargan via endpoint autenticado (compatible con GCS y storage local).
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.actividadId - ID de la actividad
 * @param {boolean} [props.disabled=false] - Deshabilitar acciones
 * @param {Function} [props.onEvidenciaChange] - Callback cuando cambia estado de evidencia
 * @returns {JSX.Element}
 */
function EvidenciaGaleria({ actividadId, disabled = false, onEvidenciaChange, canEdit = true, canDelete = true }) {
  const { showToast } = useToast();
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingMedia, setViewingMedia] = useState(null); // { ev, blobUrl }
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [editingDescId, setEditingDescId] = useState(null);
  const [editDesc, setEditDesc] = useState('');

  const fetchEvidencias = useCallback(async () => {
    try {
      const response = await apiService.getEvidenciasActividad(actividadId);
      if (response.success) {
        setEvidencias(response.data || []);
      }
    } catch (error) {
      // Silenciar — se maneja en el componente padre
    } finally {
      setLoading(false);
    }
  }, [actividadId]);

  useEffect(() => {
    fetchEvidencias();
  }, [fetchEvidencias]);

  const handleDelete = async (evidenciaId) => {
    try {
      const response = await apiService.deleteEvidencia(evidenciaId);
      if (response.success) {
        showToast('Evidencia eliminada', 'success');
        fetchEvidencias();
        if (onEvidenciaChange) onEvidenciaChange();
      }
    } catch (error) {
      showToast('Error al eliminar', 'error');
    }
  };

  const handleEditDesc = async (evidenciaId) => {
    try {
      const response = await apiService.updateEvidenciaDescripcion(evidenciaId, editDesc);
      if (response.success) {
        showToast('Descripción actualizada', 'success');
        setEditingDescId(null);
        setEditDesc('');
        fetchEvidencias();
      } else {
        showToast(response.message || 'Error al actualizar', 'error');
      }
    } catch (error) {
      showToast('Error al actualizar descripción', 'error');
    }
  };

  const handleUploaded = () => {
    fetchEvidencias();
    if (onEvidenciaChange) onEvidenciaChange();
  };

  const antesItems = evidencias.filter(e => e.tipo === 'antes');
  const despuesItems = evidencias.filter(e => e.tipo === 'despues');

  const isImage = (mime) => mime?.startsWith('image/');

  /**
   * Renderiza una sección de evidencia (antes o después)
   * @param {string} tipo - 'antes' | 'despues'
   * @param {Array} items - Evidencias del tipo
   * @param {boolean} requerida - Si es requerida
   * @returns {JSX.Element}
   */
  const renderSeccion = (tipo, items, requerida) => {
    const label = tipo === 'antes' ? 'Antes' : 'Después';
    const isOk = items.length > 0;
    const headerClass = requerida ? (isOk ? 'ev-ok' : 'ev-req') : '';
    const pillClass = requerida ? (isOk ? 'ev-ok' : 'ev-req') : '';
    const iconClass = requerida
      ? (isOk ? 'fa-circle-check' : 'fa-triangle-exclamation')
      : 'fa-camera';

    const editingItem = items.find(ev => ev.id === editingDescId);

    return (
      <div className="evidencia-seccion">
        <div className={`evidencia-seccion-header ${headerClass}`}>
          <span className="evidencia-seccion-label">
            <i className={`fas ${iconClass}`}></i>{' '}{label}
          </span>
          {requerida && (
            <span className={`evidencia-seccion-pill ${pillClass}`}>
              {isOk ? <><i className="fas fa-check" style={{ fontSize: '8px' }}></i> OK</> : 'Requerida'}
            </span>
          )}
        </div>
        <div className="evidencia-grid">
          {items.map(ev => (
            <div key={ev.id} className={`evidencia-item-wrapper${ev.id === editingDescId ? ' evidencia-item-wrapper--editing' : ''}`}>
              <EvidenciaThumb
                ev={ev}
                onView={(ev, blobUrl) => setViewingMedia({ ev, blobUrl })}
                onEdit={(ev) => { setEditingDescId(ev.id); setEditDesc(ev.descripcion || ''); }}
                onDelete={(id) => setPendingDeleteId(id)}
                disabled={disabled}
                canEdit={canEdit}
                canDelete={canDelete}
              />
              {ev.descripcion && ev.id !== editingDescId && (
                <p className="evidencia-item-caption" title={ev.descripcion}>{ev.descripcion}</p>
              )}
            </div>
          ))}
          {!disabled && (
            <EvidenciaUpload actividadId={actividadId} tipo={tipo} onUploaded={handleUploaded} />
          )}
        </div>

        {editingItem && (
          <div className="evidencia-edit-bar">
            <label className="evidencia-edit-bar-label">
              <i className="fas fa-pencil"></i> Descripción — {label}
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Descripción de la imagen..."
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditDesc(editingItem.id);
                if (e.key === 'Escape') { setEditingDescId(null); setEditDesc(''); }
              }}
              autoFocus
            />
            <div className="evidencia-edit-bar-actions">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleEditDesc(editingItem.id)}
              >
                <i className="fas fa-check"></i> Guardar
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => { setEditingDescId(null); setEditDesc(''); }}
              >
                <i className="fas fa-times"></i> Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="evidencia-loading"><i className="fas fa-spinner fa-spin"></i></div>;
  }

  return (
    <div className="evidencia-galeria">
      {/* Seccion ANTES — siempre requerida */}
      {renderSeccion('antes', antesItems, true)}

      {/* Seccion DESPUES — siempre requerida */}
      {renderSeccion('despues', despuesItems, true)}

      {/* Confirmacion de eliminacion */}
      <ConfirmDialog
        isOpen={pendingDeleteId !== null}
        title="Eliminar evidencia"
        message="¿Estás seguro de que deseas eliminar esta evidencia? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        onConfirm={() => { handleDelete(pendingDeleteId); setPendingDeleteId(null); }}
        onCancel={() => setPendingDeleteId(null)}
        variant="danger"
      />

      {/* Modal de vista completa */}
      {viewingMedia && ReactDOM.createPortal(
        <div className="evidencia-modal-backdrop" onClick={() => setViewingMedia(null)}>
          <div className="evidencia-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="evidencia-modal-close" onClick={() => setViewingMedia(null)}>
              <i className="fas fa-times"></i>
            </button>
            {isImage(viewingMedia.ev.mime_type) ? (
              <img
                src={viewingMedia.blobUrl}
                alt={viewingMedia.ev.descripcion || 'Evidencia'}
                className="evidencia-modal-img"
              />
            ) : (
              <video
                src={viewingMedia.blobUrl}
                className="evidencia-modal-video"
                controls
                autoPlay
              />
            )}
            {viewingMedia.ev.descripcion && (
              <p className="evidencia-modal-desc">{viewingMedia.ev.descripcion}</p>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default EvidenciaGaleria;
